'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@/primitives';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import { ROUTE_SEGMENT } from '@/constants/routeSegments';
import { useCurrencySymbol } from '@/hooks/publicSearch/useCurrencySymbol';
import {
  ebvGroupingOptionsFromDisplay,
  getEbvConfigGroupedByCategory,
  parseEbvAverage,
  parseEbvConfig,
  type EbvConfigGroup,
} from '@/hooks/publicSearch/ebv/ebvColumns';
import { useEbvTraitExplanations } from '@/components/publicSearch/animal/ebv/useEbvTraitExplanations';
import { useEbvDisplayOptions } from '@/hooks/publicSearch/useEbvDisplayOptions';
import InbreedingCoefficientInfoModal from './InbreedingCoefficientInfoModal';
import { MATING_DISPLAY_BY } from './types';
import { TABLE_SCROLL_BOTTOM_SPACING_PX } from './results/constants';
import MatingPredictorResultsPivoted from './results/MatingPredictorResultsPivoted';
import DesktopMatingTable from './results/DesktopMatingTable';
import MatingExportCsvButton from './results/MatingExportCsvButton';
import MatingResultsToolbar from './results/MatingResultsToolbar';
import MatingResultsPagination from './results/MatingResultsPagination';
import InbreedingInfoAlert from './results/InbreedingInfoAlert';
import { useMatingTableChrome } from './results/useMatingTableChrome';
import type {
  AnimalEbvRecord,
  MatingDisplayBy,
  MatingPredictorResponse,
  MatingPairResult,
} from './types';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useMatingPredictorStore } from '@/publicSearch/store/useMatingPredictorStore';

interface MatingPredictorResultsProps {
  data: MatingPredictorResponse;
  displayBy: MatingDisplayBy;
  onDisplayByChange: (value: MatingDisplayBy) => void;
}

export default function MatingPredictorResults({
  data,
  displayBy,
  onDisplayByChange,
}: MatingPredictorResultsProps) {
  const society = usePublicSearchContext((context) => context.society);
  const orgCode = usePublicSearchContext(
    (context) => context.society.societyCode,
  );
  const societySettings = society.settings as
    | { societySettingType?: string | null; value?: string | null }[]
    | undefined;
  const currencySymbol = useCurrencySymbol(society);
  const { showBreedplanHelp, useColourSlotGrouping } = useEbvDisplayOptions();
  const ebvGroupingOptions = useMemo(
    () => ebvGroupingOptionsFromDisplay({ useColourSlotGrouping }),
    [useColourSlotGrouping],
  );
  const { traitHeaderContext, popoverElement: ebvTraitExplanationPopover } =
    useEbvTraitExplanations(societySettings, showBreedplanHelp);
  const animalUrl = (animalId: number) =>
    orgCode ? `/${orgCode}/${ROUTE_SEGMENT.ANIMALS}/${animalId}` : undefined;

  const ebvConfig = useMemo(
    () => parseEbvConfig(societySettings),
    [societySettings],
  );

  const ebvAverage = useMemo(
    () => parseEbvAverage(societySettings),
    [societySettings],
  );

  /** EBV fields shown in this table */
  const ebvConfigForMatingGrid = useMemo(
    () =>
      ebvConfig.filter(
        (ebvConfigItem) =>
          ebvConfigItem &&
          ebvConfigItem.display > 0 &&
          ebvConfigItem.field &&
          ebvConfigItem.label &&
          /^ebv\d+$/i.test(ebvConfigItem.field.trim()),
      ),
    [ebvConfig],
  );

  /**
   * Category groups + column order match the Animal List grid: Indexes first, then
   * Calving, Growth, …, with {@link EbvConfigItem.categoryOrder} within each category.
   */
  const ebvGroups = useMemo<EbvConfigGroup[]>(
    () =>
      getEbvConfigGroupedByCategory(ebvConfigForMatingGrid, ebvGroupingOptions),
    [ebvConfigForMatingGrid, ebvGroupingOptions],
  );

  const visibleEbvs = useMemo(
    () => ebvGroups.flatMap((group) => group.items),
    [ebvGroups],
  );

  /** Fields that are the first column of a non-first category group (used to draw left separators). */
  const groupStartFields = useMemo(() => {
    const fields = new Set<string>();
    ebvGroups.forEach((group, index) => {
      if (index > 0 && group.items.length > 0)
        fields.add(group.items[0]!.field);
    });
    return fields;
  }, [ebvGroups]);

  const theme = useTheme();
  /** Pivoted Trait/Dam/Sire/EPA tables below `md`; wide EBV grid at `md` and up. */
  const isNarrowViewport = useMediaQuery(theme.breakpoints.down('md'), {
    defaultMatches: false,
  });
  const pivotedScrollRef = useRef<HTMLDivElement>(null);

  const [inbreedingModalOpen, setInbreedingModalOpen] =
    useState<boolean>(false);

  const chrome = useMatingTableChrome();

  const currentPageIndex = useMatingPredictorStore(
    (state) => state.currentPageIndex,
  );
  const setCurrentPageIndex = useMatingPredictorStore(
    (state) => state.setCurrentPageIndex,
  );
  const setTableScrollTop = useMatingPredictorStore(
    (state) => state.setTableScrollTop,
  );

  const scrollSaveRaf = useRef<number | null>(null);
  const onPivotedScroll = useCallback(() => {
    if (scrollSaveRaf.current != null) return;
    scrollSaveRaf.current = requestAnimationFrame(() => {
      scrollSaveRaf.current = null;
      const pivotedScrollContainer = pivotedScrollRef.current;
      if (pivotedScrollContainer) {
        setTableScrollTop(pivotedScrollContainer.scrollTop);
      }
    });
  }, [setTableScrollTop]);

  const sireMap = useMemo(() => {
    const siresByAnimalId = new Map<number, AnimalEbvRecord>();
    data.sires.forEach((sire) => siresByAnimalId.set(sire.animalId, sire));
    return siresByAnimalId;
  }, [data.sires]);

  const damMap = useMemo(() => {
    const damsByAnimalId = new Map<number, AnimalEbvRecord>();
    data.dams.forEach((dam) => damsByAnimalId.set(dam.animalId, dam));
    return damsByAnimalId;
  }, [data.dams]);

  /** Ordered [primaryId, pairs] for paging (Map insertion order matches first-seen in results). */
  const groupedEntries = useMemo(() => {
    const resultsByPrimaryAnimalId = new Map<number, MatingPairResult[]>();
    if (displayBy === MATING_DISPLAY_BY.DAM) {
      data.results.forEach((result) => {
        const existingPairResults =
          resultsByPrimaryAnimalId.get(result.damId) ?? [];
        existingPairResults.push(result);
        resultsByPrimaryAnimalId.set(result.damId, existingPairResults);
      });
    } else {
      data.results.forEach((result) => {
        const existingPairResults =
          resultsByPrimaryAnimalId.get(result.sireId) ?? [];
        existingPairResults.push(result);
        resultsByPrimaryAnimalId.set(result.sireId, existingPairResults);
      });
    }
    return Array.from(resultsByPrimaryAnimalId.entries());
  }, [data.results, displayBy]);

  const totalPages = groupedEntries.length;
  const safePageIndex =
    totalPages > 0 ? Math.min(currentPageIndex, totalPages - 1) : 0;
  const currentEntry =
    totalPages > 0 ? groupedEntries[safePageIndex] : undefined;
  const primaryGroupId = currentEntry?.[0];

  const currentPrimaryDisplayLabel = useMemo(() => {
    if (!currentEntry) return '';
    const [primaryId] = currentEntry;
    const primaryAnimal =
      displayBy === MATING_DISPLAY_BY.DAM
        ? damMap.get(primaryId)
        : sireMap.get(primaryId);
    return primaryAnimal
      ? `${primaryAnimal.animalName ?? ''} - ${primaryAnimal.animalIdent ?? ''}`
      : '';
  }, [currentEntry, displayBy, damMap, sireMap]);

  useEffect(() => {
    if (totalPages === 0) return;
    const maxPageIndex = totalPages - 1;
    if (currentPageIndex > maxPageIndex) {
      setCurrentPageIndex(maxPageIndex);
    }
  }, [totalPages, currentPageIndex, setCurrentPageIndex]);

  const prevDisplayByRef = useRef<MatingDisplayBy | null>(null);
  useEffect(() => {
    const previousDisplayBy = prevDisplayByRef.current;
    prevDisplayByRef.current = displayBy;
    if (previousDisplayBy != null && previousDisplayBy !== displayBy) {
      setCurrentPageIndex(0);
      setTableScrollTop(0);
    }
  }, [displayBy, setCurrentPageIndex, setTableScrollTop]);

  // Reset the pivoted (narrow) scroll position to the top whenever the page
  // changes. The desktop table owns the equivalent reset for its own scroll
  // container (only one of the two views is mounted at a time).
  const prevPageIndexRef = useRef<number | null>(null);
  useEffect(() => {
    const previousPageIndex = prevPageIndexRef.current;
    prevPageIndexRef.current = currentPageIndex;
    if (previousPageIndex === null || previousPageIndex === currentPageIndex)
      return;
    const pivotedScrollContainer = pivotedScrollRef.current;
    if (pivotedScrollContainer) pivotedScrollContainer.scrollTop = 0;
    setTableScrollTop(0);
  }, [currentPageIndex, setTableScrollTop]);
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
      }}
    >
      <MatingResultsToolbar
        displayBy={displayBy}
        onDisplayByChange={onDisplayByChange}
        showBreedplanHelp={showBreedplanHelp}
        exportButton={
          <MatingExportCsvButton
            data={data}
            visibleEbvs={visibleEbvs}
            sireMap={sireMap}
            damMap={damMap}
            currencySymbol={currencySymbol}
          />
        }
      />
      {data.warnings.length > 0 && (
        <Box sx={{ mb: 2, flexShrink: 0 }}>
          {data.warnings.map((warning, index) => (
            <Typography
              key={index}
              variant="bodymedium"
              sx={{
                color: 'warning.main',
              }}
            >
              {warning}
            </Typography>
          ))}
        </Box>
      )}
      {totalPages > 0 && currentEntry && (
        <MatingResultsPagination
          displayBy={displayBy}
          safePageIndex={safePageIndex}
          totalPages={totalPages}
          currentPrimaryDisplayLabel={currentPrimaryDisplayLabel}
          onPageChange={setCurrentPageIndex}
        />
      )}
      <InbreedingInfoAlert onLearnMore={() => setInbreedingModalOpen(true)} />
      {isNarrowViewport ? (
        <Box
          ref={pivotedScrollRef}
          onScroll={onPivotedScroll}
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            position: 'relative',
            paddingBottom: `${TABLE_SCROLL_BOTTOM_SPACING_PX}px`,
          }}
        >
          {!currentEntry ? (
            <Box sx={{ py: 2, px: 1 }}>
              <Typography
                variant="bodymedium"
                sx={{
                  color: 'text.secondary',
                }}
              >
                No prediction results to display.
              </Typography>
            </Box>
          ) : (
            <MatingPredictorResultsPivoted
              ebvGroups={ebvGroups}
              ebvAverage={ebvAverage}
              chrome={chrome}
              animalUrl={animalUrl}
              currentEntry={currentEntry}
              displayBy={displayBy}
              sireMap={sireMap}
              damMap={damMap}
              currencySymbol={currencySymbol}
              onInbreedingInfoClick={() => setInbreedingModalOpen(true)}
              traitHeaderContext={traitHeaderContext}
              ebvGroupingOptions={ebvGroupingOptions}
            />
          )}
        </Box>
      ) : (
        <DesktopMatingTable
          data={data}
          ebvGroups={ebvGroups}
          visibleEbvs={visibleEbvs}
          groupStartFields={groupStartFields}
          ebvAverage={ebvAverage}
          currencySymbol={currencySymbol}
          societyId={society.orgId}
          chrome={chrome}
          traitHeaderContext={traitHeaderContext}
          displayBy={displayBy}
          currentEntry={currentEntry}
          currentPageIndex={currentPageIndex}
          safePageIndex={safePageIndex}
          primaryGroupId={primaryGroupId}
          totalPages={totalPages}
          sireMap={sireMap}
          damMap={damMap}
          animalUrl={animalUrl}
          onInbreedingInfoClick={() => setInbreedingModalOpen(true)}
          ebvGroupingOptions={ebvGroupingOptions}
        />
      )}
      <InbreedingCoefficientInfoModal
        open={inbreedingModalOpen}
        onClose={() => setInbreedingModalOpen(false)}
      />
      {ebvTraitExplanationPopover}
    </Box>
  );
}
